import React, { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Plus, Edit, Eye, Trash2, Search, Filter, X } from 'lucide-react';

interface Physiotherapist {
  id: number;
  first_name: string;
  last_name: string;
  city?: string;
  certified: boolean;
  profile_complete: boolean;
  user?: {
    id: number;
    email_address: string;
  };
  has_approved_specialty: boolean;
  has_pending_request: boolean;
  latest_approved_at?: string;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface Filters {
  search: string;
  status: string;
  approved_from: string;
  approved_to: string;
}

interface Props {
  physiotherapists: Physiotherapist[];
  pagination: Pagination;
  filters: Filters;
}

const getStatusBadge = (physio: Physiotherapist) => {
  if (physio.has_approved_specialty) {
    return { label: 'Approuvé', className: 'bg-green-100 text-green-800' };
  }
  if (physio.has_pending_request) {
    return { label: 'En attente de certif', className: 'bg-amber-100 text-amber-800' };
  }
  if (physio.profile_complete) {
    return { label: 'Profil complet', className: 'bg-blue-100 text-blue-800' };
  }
  return { label: 'Profil incomplet', className: 'bg-gray-100 text-gray-600' };
};

const PhysiotherapistsIndex: React.FC<Props> = ({ physiotherapists, pagination, filters }) => {
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [status, setStatus] = useState(filters.status || '');
  const [approvedFrom, setApprovedFrom] = useState(filters.approved_from || '');
  const [approvedTo, setApprovedTo] = useState(filters.approved_to || '');
  const [showFilters, setShowFilters] = useState(
    !!(filters.status || filters.approved_from || filters.approved_to)
  );

  const hasActiveFilters = !!(filters.status || filters.approved_from || filters.approved_to);

  const applyFilters = (overrides: Record<string, string> = {}) => {
    const params: Record<string, string> = {
      search: searchValue,
      status,
      approved_from: approvedFrom,
      approved_to: approvedTo,
      ...overrides,
    };
    // Remove empty values
    Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
    router.get('/admin/physiotherapists', params, { preserveState: true });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    applyFilters();
  };

  const handleClearAll = () => {
    setSearchValue('');
    setStatus('');
    setApprovedFrom('');
    setApprovedTo('');
    router.get('/admin/physiotherapists', {}, { preserveState: true });
  };

  const buildPageUrl = (page: number) => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.approved_from) params.set('approved_from', filters.approved_from);
    if (filters.approved_to) params.set('approved_to', filters.approved_to);
    return `/admin/physiotherapists?${params.toString()}`;
  };

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const total = pagination.total_pages;
    const current = pagination.current_page;

    pages.push(1);
    if (current > 3) pages.push('ellipsis');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.push(i);
    }
    if (current < total - 2) pages.push('ellipsis');
    if (total > 1) pages.push(total);

    return pages;
  };

  return (
    <AdminLayout title="Gestion des kinésithérapeutes">
      <Head title="Kinésithérapeutes - Admin" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Kinésithérapeutes</h1>
            <p className="text-gray-600">
              {pagination.total_count} kinésithérapeute{pagination.total_count > 1 ? 's' : ''} au total
            </p>
          </div>
          <Link href="/admin/physiotherapists/new">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau kinésithérapeute
            </Button>
          </Link>
        </div>

        {/* Search & Filters */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Rechercher par nom ou ville..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit">Rechercher</Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={hasActiveFilters ? 'border-[#008788] text-[#008788]' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtres
                {hasActiveFilters && <span className="ml-1.5 w-2 h-2 rounded-full bg-[#008788]" />}
              </Button>
              {(filters.search || hasActiveFilters) && (
                <Button type="button" variant="ghost" onClick={handleClearAll}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </form>

            {showFilters && (
              <div className="flex flex-wrap gap-4 pt-2 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Statut</label>
                  <select
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); applyFilters({ status: e.target.value }); }}
                    className="rounded-md border border-gray-200 text-sm px-3 py-1.5"
                  >
                    <option value="">Tous</option>
                    <option value="approved">Approuvé</option>
                    <option value="pending">En attente de certif</option>
                    <option value="complete">Profil complet</option>
                    <option value="incomplete">Profil incomplet</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Certifié du</label>
                  <Input
                    type="date"
                    value={approvedFrom}
                    onChange={(e) => { setApprovedFrom(e.target.value); applyFilters({ approved_from: e.target.value }); }}
                    className="text-sm h-8"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Au</label>
                  <Input
                    type="date"
                    value={approvedTo}
                    onChange={(e) => { setApprovedTo(e.target.value); applyFilters({ approved_to: e.target.value }); }}
                    className="text-sm h-8"
                  />
                </div>
              </div>
            )}

            {filters.search && (
              <p className="text-sm text-gray-500">
                {pagination.total_count} résultat{pagination.total_count > 1 ? 's' : ''} pour « {filters.search} »
              </p>
            )}
          </CardContent>
        </Card>

        {/* Physiotherapists Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des kinésithérapeutes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">ID</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Nom</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Ville</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Statut</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Certifié le</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Créé le</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {physiotherapists.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-500">
                        Aucun kinésithérapeute trouvé.
                      </td>
                    </tr>
                  ) : (
                    physiotherapists.map((physio) => {
                      const badge = getStatusBadge(physio);
                      return (
                        <tr key={physio.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-900">#{physio.id}</td>
                          <td className="py-3 px-4 text-sm text-gray-900">
                            {physio.first_name} {physio.last_name}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {physio.user?.email_address || <span className="text-gray-400 italic">Aucun compte</span>}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {physio.city || 'Non spécifié'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {physio.latest_approved_at
                              ? new Date(physio.latest_approved_at).toLocaleDateString('fr-FR')
                              : '—'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {new Date(physio.created_at).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Link href={`/admin/physiotherapists/${physio.id}`}>
                                <Button size="sm" variant="ghost">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Link href={`/admin/physiotherapists/${physio.id}/edit`}>
                                <Button size="sm" variant="ghost">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Link
                                href={`/admin/physiotherapists/${physio.id}`}
                                method="delete"
                                as="button"
                                preserveScroll
                                onBefore={() => {
                                  if (!confirm('Êtes-vous sûr de vouloir supprimer ce kinésithérapeute ?')) {
                                    return false;
                                  }
                                }}
                              >
                                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Page {pagination.current_page} sur {pagination.total_pages}
                </div>
                <div className="flex items-center gap-1">
                  {pagination.current_page > 1 && (
                    <Link href={buildPageUrl(pagination.current_page - 1)}>
                      <Button variant="outline" size="sm">
                        Précédent
                      </Button>
                    </Link>
                  )}
                  {getPageNumbers().map((page, index) =>
                    page === 'ellipsis' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-gray-400">…</span>
                    ) : (
                      <Link key={page} href={buildPageUrl(page)}>
                        <Button
                          variant={page === pagination.current_page ? 'default' : 'outline'}
                          size="sm"
                          className="min-w-[36px]"
                        >
                          {page}
                        </Button>
                      </Link>
                    )
                  )}
                  {pagination.current_page < pagination.total_pages && (
                    <Link href={buildPageUrl(pagination.current_page + 1)}>
                      <Button variant="outline" size="sm">
                        Suivant
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PhysiotherapistsIndex;
